/**
 * NotificationDrawer — UAT-3A drawer that lists in-app notifications.
 *
 * Uses shadcn Sheet (right-side) + Tabs (All | Unread). Each row:
 *   • Click → mark read + navigate to notification.link.
 *   • Unread indicator dot on the left.
 *   • Relative timestamp.
 *
 * "Mark all as read" lives in the header. Empty/loading states handled.
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Icon } from '../common/Icon';
import { normalizeNotificationLink } from '../../lib/notificationLinks';

function formatRelative(iso, t, language) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const m = Math.floor(diff / 60000);
    if (m < 1) return t('relative.justNow');
    if (m < 60) return t('relative.minutesAgo', { count: m });
    const h = Math.floor(m / 60);
    if (h < 24) return t('relative.hoursAgo', { count: h });
    const d = Math.floor(h / 24);
    if (d < 7) return t('relative.daysAgo', { count: d });
    return new Date(iso).toLocaleDateString(language || undefined);
  } catch {
    return '';
  }
}

function NotificationRow({ item, onClick }) {
  const { t, i18n } = useTranslation('notifications');
  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      className={`w-full text-start px-4 py-3 border-b border-border hover:bg-muted/50 transition-colors flex gap-3 ${
        item.read ? 'opacity-70' : ''
      }`}
      data-testid={`notification-row-${item.id}`}
    >
      <div className="mt-1.5 shrink-0">
        <span
          aria-hidden="true"
          className={`block h-2 w-2 rounded-full ${item.read ? 'bg-transparent' : 'bg-primary'}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${item.read ? 'font-normal' : 'font-semibold'} text-foreground line-clamp-2`}>
            {item.title}
          </p>
          <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">
            {formatRelative(item.created_at, t, i18n.resolvedLanguage || i18n.language)}
          </span>
        </div>
        {item.body ? (
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{item.body}</p>
        ) : null}
        {item.category ? (
          <span className="inline-block mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            {item.category}
          </span>
        ) : null}
      </div>
    </button>
  );
}

export function NotificationDrawer({ open, onOpenChange, controller }) {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const {
    items, unread, loading, filter,
    fetchList, markRead, markAllRead, clearAll, loadMore, hasMore, setFilter,
  } = controller;

  // Initial fetch when drawer opens
  useEffect(() => {
    if (open) fetchList({ page: 1, filter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleRowClick = async (item) => {
    if (!item.read) markRead(item.id);
    // Normalize current and legacy links, with an event/role-aware fallback
    // for older Dealer notifications that were persisted without a link.
    const target = normalizeNotificationLink(item.link, item);
    if (!target) return;
    onOpenChange(false);
    try {
      if (target.startsWith('/')) {
        navigate(target);
      } else {
        window.location.href = target;
      }
    } catch {
      // ignore
    }
  };

  const visibleItems = filter === 'unread' ? items.filter((i) => !i.read) : items;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="notification-drawer">
        <SheetHeader className="px-4 pt-5 pb-3 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Icon name="bell" className="h-4 w-4" />
              {t('drawer.title')}
              {unread > 0 ? (
                <span className="ms-1 text-xs font-medium text-primary" data-testid="notification-drawer-unread-count">
                  ({unread})
                </span>
              ) : null}
            </SheetTitle>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={markAllRead}
              disabled={unread === 0}
              data-testid="notification-mark-all-read"
            >
              <Icon name="check-check" className="h-3.5 w-3.5 me-1" />
              {t('actions.markAllRead')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-destructive"
              onClick={() => {
                if (window.confirm(t('actions.clearAllConfirm'))) clearAll();
              }}
              disabled={items.length === 0}
              data-testid="notification-clear-all"
            >
              <Icon name="trash-2" className="h-3.5 w-3.5 me-1" />
              {t('actions.clearAll')}
            </Button>
          </div>

          <Tabs value={filter} onValueChange={setFilter} className="mt-2" data-testid="notification-filter-tabs">
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="all" data-testid="notification-tab-all">{t('filters.all')}</TabsTrigger>
              <TabsTrigger value="unread" data-testid="notification-tab-unread">
                {t('filters.unread')}{unread > 0 ? ` (${unread})` : ''}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </SheetHeader>

        <ScrollArea className="flex-1">
          {loading && items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notification-loading">
              {t('states.loading')}
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-2" data-testid="notification-empty">
              <Icon name="inbox" className="h-8 w-8 text-muted-foreground/60" />
              <p>{filter === 'unread' ? t('states.allCaughtUp') : t('states.empty')}</p>
            </div>
          ) : (
            <div>
              {visibleItems.map((item) => (
                <NotificationRow key={item.id} item={item} onClick={handleRowClick} />
              ))}
              {hasMore ? (
                <div className="p-4 text-center">
                  <Button variant="outline" size="sm" onClick={loadMore} disabled={loading}>
                    {t('actions.loadMore')}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

export default NotificationDrawer;
